import { useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { PresupuestoForm } from '@/components/presupuestos/PresupuestoForm'
import type { PresupuestoFormValues } from '@/components/presupuestos/PresupuestoForm'
import { usePresupuesto, useActualizarPresupuesto } from '@/hooks/usePresupuestos'
import { isPresupuestoEditable } from '@/components/presupuestos/presupuesto-utils'
import { toast } from 'sonner'
import type { Json } from '@/types/database'

export function PresupuestoEditar() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const { data: presupuesto, isLoading: isLoadingPresupuesto, error } = usePresupuesto(id || '')
  const actualizarPresupuesto = useActualizarPresupuesto()

  const handleSubmit = async (data: PresupuestoFormValues & { subtotal: number; total: number }) => {
    if (!id) return

    try {
      const presupuestoData = {
        cliente_nombre: data.cliente_nombre,
        cliente_cuit: data.cliente_cuit,
        cliente_email: data.cliente_email,
        cliente_id: data.cliente_id,
        condiciones_pago: data.condiciones_pago,
        fecha: data.fecha,
        validez_dias: data.validez_dias,
        items: data.items as unknown as Json,
        subtotal: data.subtotal,
        descuento_porcentaje: data.descuento_porcentaje,
        iva_porcentaje: parseFloat(data.iva_porcentaje) || 0,
        total: data.total,
        notas: data.notas,
        admin_nombre: data.admin_nombre,
        admin_cuit: data.admin_cuit,
      }

      await actualizarPresupuesto.mutateAsync({
        id,
        data: presupuestoData,
      })
      toast.success('Presupuesto actualizado correctamente')
      navigate('/presupuestos')
    } catch {
      // El onError de useActualizarPresupuesto muestra el toast, y dice si faltó un permiso.
    }
  }

  const handleCancel = () => {
    navigate('/presupuestos')
  }

  // Loading state
  if (isLoadingPresupuesto) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="Editar Presupuesto"
          description="Cargando..."
        />
        <main className="flex-1 p-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </main>
      </div>
    )
  }

  // Guard: solo se puede editar en estado borrador
  if (presupuesto && !isPresupuestoEditable(presupuesto.estado)) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="Editar Presupuesto"
          description="No editable"
        />
        <main className="flex-1 p-6">
          <div className="mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver a Presupuestos
            </Button>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-4">
            <p className="text-amber-700 dark:text-amber-300">
              Este presupuesto no puede ser editado porque su estado es <strong>{presupuesto.estado}</strong>.
              Solo los presupuestos en estado <strong>borrador</strong> pueden ser editados.
              Para modificar, duplique el presupuesto.
            </p>
          </div>
        </main>
      </div>
    )
  }

  // Error state
  if (error || !presupuesto) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="Editar Presupuesto"
          description="Error al cargar"
        />
        <main className="flex-1 p-6">
          <div className="mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver a Presupuestos
            </Button>
          </div>
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-destructive">
              No se pudo cargar el presupuesto. Es posible que no exista o no tengas permisos para verlo.
            </p>
          </div>
        </main>
      </div>
    )
  }

  const numeroFormateado = ((presupuesto.numero_presupuesto || 0).toString().padStart(4, '0'))

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title={`Editar Presupuesto #${numeroFormateado}`}
        description={`Cliente: ${presupuesto.cliente_nombre}`}
      />

      <main className="flex-1 p-6">
        {/* Breadcrumb / Back button */}
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a Presupuestos
          </Button>
        </div>

        {/* Formulario */}
        <PresupuestoForm
          presupuesto={presupuesto}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={actualizarPresupuesto.isPending}
        />
      </main>
    </div>
  )
}
